/** Class component error boundary that catches rendering errors and shows fallback UI. */

import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
  onRetry?: () => void
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
    if (this.props.onRetry) {
      this.props.onRetry()
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const showStack = import.meta.env.MODE !== 'production'

      return (
        <div
          className="flex flex-col items-center justify-center gap-4 rounded-lg bg-gray-800 p-8 text-center"
          role="alert"
          data-testid="error-boundary"
        >
          <h2 className="text-lg font-semibold text-red-400">Something went wrong</h2>
          <p className="text-sm text-gray-400" data-testid="error-message">
            {this.state.error?.message ?? 'An unexpected error occurred'}
          </p>
          {showStack && this.state.error?.stack && (
            <pre
              className="mt-2 max-h-40 w-full overflow-auto rounded bg-gray-900 p-3 text-left text-xs text-gray-500"
              data-testid="error-stack"
            >
              {this.state.error.stack}
            </pre>
          )}
          <button
            onClick={this.handleRetry}
            className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            data-testid="error-retry"
          >
            Retry
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
